import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { getApiBase } from "@/lib/phpApi";
import { Eye, EyeOff, Droplets } from "lucide-react";

/* ─── Animated Water Background Canvas ──────────────────── */
function WaterCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animationId: number;
    let time = 0;

    // Resolve CSS variables — Canvas API cannot use var() references
    const cssVars = getComputedStyle(document.documentElement);
    const surfaceRGB = cssVars.getPropertyValue('--rb-surface-rgb').trim() || '10,22,40';
    const activeRGB  = cssVars.getPropertyValue('--rb-active-rgb').trim()  || '30,58,95';
    const brandRGB   = cssVars.getPropertyValue('--rb-brand-rgb').trim()   || '205,164,46';

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    function draw() {
      if (!ctx || !canvas) return;
      const w = canvas.width;
      const h = canvas.height;
      time += 0.008;

      const grad = ctx.createLinearGradient(0, 0, 0, h);
      grad.addColorStop(0,   `rgb(${surfaceRGB})`);
      grad.addColorStop(0.5, "#0d1f3a");
      grad.addColorStop(1,   "#162d4e");
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, w, h);

      const layers = [
        { color: `rgba(${activeRGB},0.3)`,  speed: 0.5, amplitude: 30, yOffset: 0.3 },
        { color: "rgba(20,184,166,0.15)",    speed: 0.8, amplitude: 20, yOffset: 0.4 },
        { color: `rgba(${brandRGB},0.08)`,   speed: 1.2, amplitude: 15, yOffset: 0.5 },
        { color: `rgba(${activeRGB},0.2)`,   speed: 0.3, amplitude: 40, yOffset: 0.6 },
      ];

      layers.forEach((layer) => {
        ctx.beginPath();
        ctx.moveTo(0, h);
        for (let x = 0; x <= w; x += 2) {
          const y =
            h * layer.yOffset +
            Math.sin(x * 0.003 + time * layer.speed) * layer.amplitude +
            Math.sin(x * 0.007 + time * layer.speed * 1.5) * (layer.amplitude * 0.5) +
            Math.sin(x * 0.001 + time * layer.speed * 0.5) * (layer.amplitude * 0.3);
          ctx.lineTo(x, y);
        }
        ctx.lineTo(w, h);
        ctx.closePath();
        ctx.fillStyle = layer.color;
        ctx.fill();
      });

      const particleCount = 30;
      for (let i = 0; i < particleCount; i++) {
        const px = ((Math.sin(time * 0.3 + i * 2.1) * 0.5 + 0.5) * w * 0.8) + w * 0.1;
        const py = ((Math.cos(time * 0.2 + i * 1.7) * 0.5 + 0.5) * h * 0.6) + h * 0.2;
        const size = 1 + Math.sin(time + i) * 0.5;
        const alpha = 0.1 + Math.sin(time * 0.5 + i) * 0.08;

        ctx.beginPath();
        ctx.arc(px, py, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${brandRGB},${alpha.toFixed(3)})`;
        ctx.fill();
      }

      const shimmerCount = 5;
      for (let i = 0; i < shimmerCount; i++) {
        const sx = ((time * 20 + i * 150) % (w + 200)) - 100;
        const sy = h * 0.35 + Math.sin(time + i) * 30;
        const length = 60 + Math.sin(time * 0.7 + i) * 30;

        ctx.beginPath();
        ctx.moveTo(sx, sy);
        ctx.lineTo(sx + length, sy + 3);
        ctx.strokeStyle = `rgba(${brandRGB},${(0.06 + Math.sin(time + i) * 0.03).toFixed(3)})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      }

      animationId = requestAnimationFrame(draw);
    }

    draw();
    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 0,
        pointerEvents: "none",
      }}
    />
  );
}

/* ─── Main Component ────────────────────────────────────── */
export default function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  // Auto-configure API URL on first visit
  useEffect(() => {
    const configured = localStorage.getItem("rb_api_url");
    if (!configured) {
      // Relative path — works for both root and subdirectory deployments
      localStorage.setItem("rb_api_url", "api");
    }
  }, []);

  /* ─── Login ─────────────────────────────────────────────── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    const ok = await auth.login(username, password);
    if (ok) { navigate('/', { replace: true }); }
  };

  /* ─── Render ────────────────────────────────────────────── */
  return (
    <div className="min-h-screen flex flex-col relative">
      {/* Animated water background */}
      <WaterCanvas />

      {/* Content layer */}
      <div className="relative z-10 flex flex-col min-h-screen">
        {/* Top bar */}
        <div className="w-full px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-rb-brand flex items-center justify-center overflow-hidden shadow-lg">
              <img src="https://utoo.co.uk/images/bb.jpg" alt="RB" className="w-full h-full object-cover" />
            </div>
            <span className="text-sm font-bold text-white/80">Reynolds &amp; Bauhm</span>
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex items-center justify-center px-4 py-6">
          <div className="w-full max-w-[420px]">

            {/* Hero image */}
            <div className="text-center mb-6">
              <div className="mx-auto mb-4 w-28 h-28 rounded-2xl overflow-hidden border-2 border-rb-brand/40 shadow-[0_8px_32px_rgba(205,164,46,0.3)] bg-rb-elevated/80 backdrop-blur-sm relative">
                <div className="absolute inset-0 bg-amber-500/10 animate-pulse" />
                <img
                  src="https://utoo.co.uk/images/bb.jpg"
                  alt="Reynolds & Bauhm"
                  className="w-full h-full object-cover relative z-10"
                  onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                />
              </div>
              <h1 className="text-2xl font-bold text-white mb-1 drop-shadow-lg">Reynolds & Bauhm</h1>
              <p className="text-sm text-white/60">Smart Wastewater Assessment Tool v2.0</p>
            </div>

            {/* Login form */}
            <div className="bg-rb-surface/95 backdrop-blur rounded-2xl shadow-[0_24px_80px_rgba(0,0,0,0.4)] overflow-hidden border border-white/10">
              <div className="h-1.5 bg-rb-active" />
              <div className="p-7">
                <div className="flex items-center gap-2 mb-5">
                  <Droplets className="w-4 h-4 text-rb-brand" />
                  <h2 className="text-sm font-semibold text-slate-400">Smart Tool v2.0</h2>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <div className="text-sm text-red-600 bg-red-500/10 px-3 py-2 rounded-lg">
                      {error}
                    </div>
                  )}
                  {auth.error && (
                    <div className="text-sm text-red-600 bg-red-500/10 px-3 py-2 rounded-lg">
                      {auth.error}
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="username" className="text-slate-400 text-xs font-medium">Username</Label>
                    <Input
                      id="username" type="text" value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required autoComplete="username"
                      placeholder="Enter username"
                      className="text-black bg-rb-elevated h-11"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-slate-400 text-xs font-medium">Password</Label>
                    <div className="relative">
                      <Input
                        id="password" type={showPassword ? "text" : "password"} value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required autoComplete="current-password"
                        placeholder="Enter password"
                        className="pr-10 text-black bg-rb-elevated h-11"
                      />
                      <button type="button" onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" tabIndex={-1}>
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  <Button type="submit" className="w-full bg-rb-active hover:bg-rb-surface text-white h-11 font-semibold" disabled={auth.isLoading}>
                    {auth.isLoading ? "Signing in..." : "Sign In"}
                  </Button>
                </form>

                <p className="text-[11px] text-slate-400 text-center mt-4">
                  Contact administrator for account access.
                </p>
              </div>
            </div>

            {/* Footer disclaimer */}
            <div className="mt-5 px-4">
              <div className="rounded-xl border border-rb-brand/20 bg-rb-brand/[0.05] backdrop-blur-sm p-3">
                <p className="text-[10px] text-white/40 leading-relaxed text-center">
                  <strong className="text-rb-brand">IP Notice:</strong> Software and algorithms are the exclusive property of <strong className="text-white/60">Reynolds &amp; Bauhm</strong>. For <strong className="text-white/60">early assessment only</strong>. Laboratory testing required prior to any decision. No liability accepted.
                </p>
              </div>
            </div>
            <p className="text-center text-[10px] text-white/20 mt-4 pb-4">
              &copy; {new Date().getFullYear()} Reynolds &amp; Bauhm &middot; Software v1.0
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
